import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Receipt, FileText, Check } from 'lucide-react';
import { fmtDate } from '../../lib/orderMoney';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProofList = ({ proofs, srcFor, testPrefix }) => (
  <div className="space-y-2.5">
    {proofs.map(p => (
      <div key={p.id} data-testid={`${testPrefix}-${p.id}`} className="flex gap-3 items-start rounded-lg border border-neutral-200 dark:border-neutral-800 p-2.5">
        {p.content_type && !srcFor(p) ? <div className="shrink-0 w-16 h-16 rounded-md bg-neutral-100 dark:bg-neutral-800 animate-pulse" /> : p.content_type ? (
          <a href={srcFor(p)} target="_blank" rel="noopener noreferrer" className="shrink-0 w-16 h-16 rounded-md overflow-hidden bg-neutral-100 dark:bg-neutral-800 grid place-items-center">
            {p.content_type.startsWith('image/') && p.content_type !== 'image/heic'
              ? <img src={srcFor(p)} alt="Payment screenshot" className="w-full h-full object-cover" />
              : <FileText size={22} className="text-neutral-500" />}
          </a>
        ) : <div className="shrink-0 w-16 h-16 rounded-md bg-neutral-100 dark:bg-neutral-800 grid place-items-center"><Receipt size={20} className="text-neutral-400" /></div>}
        <div className="min-w-0 text-[12.5px]">
          <div className="text-neutral-500">Transaction ID</div>
          <div data-testid={`${testPrefix}-txn-${p.id}`} className="font-mono text-neutral-900 dark:text-neutral-100 break-all">{p.transaction_id || '—'}</div>
          <div className="text-[11px] text-neutral-500 mt-1">{fmtDate(p.uploaded_at)}{p.content_type ? ' · screenshot attached' : ''}</div>
        </div>
      </div>
    ))}
  </div>
);

export const PaymentProofCard = ({ order, proofs, token, headers, onDone }) => {
  const [txn, setTxn] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!txn.trim() && !file) { setMsg('Add a screenshot or a transaction ID'); return; }
    if (file && file.size > 10 * 1024 * 1024) { setMsg('File is too large (max 10 MB)'); return; }
    const fd = new FormData();
    fd.append('t', token); fd.append('transaction_id', txn.trim());
    if (file) fd.append('file', file);
    setBusy(true); setMsg('');
    try {
      await axios.post(`${API}/order-portal/${order.id}/payment-proof`, fd, { headers });
      setTxn(''); setFile(null); e.target.reset(); setMsg('ok'); onDone();
    } catch (ex) { setMsg(ex?.response?.data?.detail || 'Upload failed. Please try again.'); }
    finally { setBusy(false); }
  };

  const src = (p) => `${API}/order-portal/${order.id}/proof/${p.id}?t=${encodeURIComponent(token)}`;
  return (
    <div data-testid="payment-proof-card" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 space-y-3">
      <div className="flex items-center gap-2"><Receipt size={15} className="text-brand" /><h3 className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100">Payment proof</h3></div>
      {proofs.length > 0 && <ProofList proofs={proofs} srcFor={src} testPrefix="customer-proof" />}
      <form onSubmit={submit} className="space-y-2.5">
        <input data-testid="proof-txn-input" value={txn} onChange={(e) => setTxn(e.target.value)} maxLength={200} placeholder="Transaction ID / hash / reference"
          className="w-full bg-neutral-50 dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-[13px] font-mono outline-none focus:border-brand" />
        <label className="flex items-center gap-2 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 px-3 py-2.5 cursor-pointer hover:border-brand text-[12.5px] text-neutral-600 dark:text-neutral-300">
          <Upload size={14} /> <span className="truncate">{file ? file.name : 'Attach payment screenshot (JPG, PNG, PDF · max 10 MB)'}</span>
          <input data-testid="proof-file-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="flex items-center gap-3">
          <button data-testid="proof-submit-btn" disabled={busy} className="btn-brand rounded-lg px-4 py-2 text-[13px] font-semibold disabled:opacity-50">{busy ? 'Uploading…' : 'Submit payment proof'}</button>
          {msg === 'ok' ? <span data-testid="proof-success" className="text-[12px] text-emerald-600 inline-flex items-center gap-1"><Check size={13} /> Received — thank you!</span>
            : msg && <span data-testid="proof-error" className="text-[12px] text-red-600">{msg}</span>}
        </div>
      </form>
    </div>
  );
};
